import { Component, Input } from '@angular/core';

import ActionTypesEnum from '@shared/enums/action-constant.enum';

import { CustomEventService } from '@shared/services/custom-event.service';
import { GlobalSettingsService } from '@shared/services/global-settings.service';

import DateUtil from '@shared/utils/date.util';
import durations from '@shared/variables/durations.variable';

import {
  addMonths,
  differenceInCalendarDays,
  isSameMonth,
  isSameYear, lastDayOfYear,
  startOfMonth, startOfYear,
  subMonths,
  subYears
} from 'date-fns';
import { fromJS } from 'immutable';
import { formatDate } from "@angular/common";

@Component({
  selector: 'app-durations',
  templateUrl: './durations.component.html'
})
export class DurationsComponent {

  @Input() loading: boolean;

  _keysOfHiddenButtons = [];
  get keysOfHiddenButtons(): string[] {
    return this._keysOfHiddenButtons;
  };

  @Input('keysOfHiddenButtons')
  set keysOfHiddenButtons(keysOfHiddenButtons: string[]) {
    this._keysOfHiddenButtons = keysOfHiddenButtons;
    this.durationButtons = fromJS(durations).toJS().filter(item => keysOfHiddenButtons.indexOf(item.key) === -1);
  };

  _type = 'default';
  get type(): string {
    return this._type;
  }

  @Input('type')
  set type(type: string) {
    this._type = type;
  };

  _selectedDate: Base.DurationsModel;
  get selectedDate(): Base.DurationsModel {
    return this._selectedDate;
  }

  @Input('selectedDate')
  set selectedDate(date: Base.DurationsModel) {
    this._selectedDate = date;

    setTimeout((): void => {
      this.activeButton = this._selectedDate && this._selectedDate.period
        ? this.durationButtons.findIndex(item => item.key === this._selectedDate.period)
        : undefined;
    }, 100);

    if (this._selectedDate && this._selectedDate.fromDate && !this._selectedDate.toDate) {
      this.setCustomButtonText(new Date(this._selectedDate.fromDate), this._selectedDate.period);
    }

    if (this._selectedDate && this._selectedDate.fromDate && this._selectedDate.toDate) {
      this.setCustomButtonText([new Date(this._selectedDate.fromDate), new Date(this._selectedDate.toDate)], this._selectedDate.period);
    }
  }

  activeButton: number;

  durationButtons: Base.DurationButtonModel[];
  pickers = new Map([
    ['CUSTOM_DATE', false],
    ['MONTHLY', false],
    ['YEARLY', false]
  ]);
  date: Date | Date[];
  monthlyDate: Date = subMonths(new Date(), 1);
  yearlyDate: Date = subMonths(new Date(), 1);

  monthFormat: string;
  yearFormat: string;
  dateFormat: string;

  isNextDay = (date: Date) => differenceInCalendarDays(date, new Date()) > 0;

  isLessThanYear = (date: Date, amount: number) => differenceInCalendarDays(date, new Date(subYears(new Date(), amount))) < 0;

  disabledDate = (current: Date): boolean => this.isNextDay(current);

  monthlyDisableDate = (current: Date): boolean => this.isNextDay(current) || this.isLessThanYear(current, 1);

  yearlyDisableDate = (current: Date): boolean => this.isNextDay(current) || this.isLessThanYear(current, 3);

  constructor(private customEventService: CustomEventService,
              private globalSettings: GlobalSettingsService) {
    const { monthFormat, yearFormat, dateFormat } = this.globalSettings;

    this.monthFormat = monthFormat;
    this.yearFormat = yearFormat;
    this.dateFormat = dateFormat;

    this.durationButtons = fromJS(durations).toJS();
  }

  selectDate(button: Base.DurationButtonModel): void {
    if (button.isPicker) {
      this.openPicker(button);
      return;
    }

    this.resetButtonText();

    this.setActiveButton(button.key);

    this.closePickers();

    this.customEventService.announceEvent(ActionTypesEnum.UPDATE_DURATIONS_DATE, button.key);
  }

  onOk($event: Date | Date[], key: string): void {
    this.setActiveButton(key);

    this.resetButtonText();

    this.setCustomButtonText($event, key);

    this.pickers.set(key, false);

    let fromDate;
    let toDate;

    switch (key) {
      case 'MONTHLY': {
        const date: Date = $event as Date;

        fromDate = DateUtil.formatDate(startOfMonth(date));

        toDate = isSameMonth(date, new Date())
          ? DateUtil.formatDate(new Date())
          : DateUtil.formatDate(addMonths(startOfMonth(date), 1));

        break;
      }
      case 'YEARLY': {
        const date: Date = $event as Date;

        fromDate = DateUtil.formatDate(startOfYear(date));

        toDate = isSameYear(date, new Date())
          ? DateUtil.formatDate(new Date())
          : DateUtil.formatDate(lastDayOfYear(date));

        break;
      }
      default: {
        if (this.type === 'range') {
          fromDate = DateUtil.formatDate($event[0]);
          toDate = DateUtil.formatDate($event[1]);
        } else {
          fromDate = DateUtil.formatDate($event);
        }
      }
    }

    if (fromDate && toDate) {
      this.customEventService.announceEvent(ActionTypesEnum.UPDATE_DURATIONS_DATE, { fromDate, toDate });
      return;
    }

    this.customEventService.announceEvent(ActionTypesEnum.UPDATE_DURATIONS_DATE, fromDate);
  }

  setActiveButton(key: string): void {
    this.activeButton = this.durationButtons.findIndex(item => item.key === key);
  }

  setCustomButtonText(date: Date | Date[], key: string): void {
    const buttonIndex = this.durationButtons.findIndex(item => item.key === key);

    const format = key === 'MONTHLY' ? this.monthFormat : (key === 'YEARLY' ? this.yearFormat : this.dateFormat);

    this.durationButtons[buttonIndex].displayText = Array.isArray(date)
      ? `${DateUtil.formatDate(date[0], format)} - ${DateUtil.formatDate(date[1], format)}`
      : DateUtil.formatDate(date, format);
  }

  resetButtonText(): void {
    this.durationButtons
      .filter(item => item.displayText)
      .forEach(item => delete item.displayText);
  }

  openPicker(button: Base.DurationButtonModel): void {
    this.pickers.forEach((value, key) => this.pickers.set(key, key === button.key));
  }

  closePickers(): void {
    this.pickers.forEach((value, key) => this.pickers.set(key, false));
  }
}
